/**
 * Royalty-free stock photos that ship with the app: used by gallery templates (as
 * `gallery:<id>`, like the vector illustrations) and offered in the editor's media panel.
 *
 * Source: Pixabay images (Pixabay Content License — free for commercial use, no attribution
 * required) via the yavuzceliker/sample-images collection; see apps/web/public/gallery/photos/
 * NOTICE.md. Files live in apps/web/public/gallery/photos (served at /gallery/photos/), with
 * 360 px previews in ./thumbs.
 */

export const PHOTO_TAGS = [
  'people',
  'beauty',
  'food',
  'travel',
  'home',
  'business',
  'sport',
  'holiday',
  'auto',
  'pets',
  'nature',
] as const;
export type PhotoTag = (typeof PHOTO_TAGS)[number];

export interface StockPhoto {
  width: number;
  height: number;
  tags: readonly PhotoTag[];
  /** Short English description, used in the AI catalog and as alt text. */
  alt: string;
  /** Focal point used when the photo is cropped (faces, the subject). */
  focusX?: number;
  focusY?: number;
}

const p = (size: string, tags: PhotoTag[], alt: string, focus?: [number, number]): StockPhoto => {
  const [width, height] = size.split('x').map(Number) as [number, number];
  return { width, height, tags, alt, focusX: focus?.[0], focusY: focus?.[1] };
};

export const PHOTOS = {
  'fashion-selfie': p(
    '1200x800',
    ['people'],
    'Smiling woman taking a selfie on a red wall',
    [0.55, 0.3],
  ),
  'leather-shoes': p('1200x1200', ['people'], 'Brown leather shoes on a blue background'),
  'fashion-model': p('800x1200', ['people', 'beauty'], 'Blonde model in a white top', [0.5, 0.3]),
  'retro-style': p(
    '800x1200',
    ['people', 'auto'],
    'Woman in sunglasses by a pink vintage car',
    [0.5, 0.35],
  ),
  lipstick: p('1200x800', ['beauty'], 'Red lipsticks on red silk'),
  'beauty-portrait': p(
    '800x1200',
    ['beauty', 'people'],
    'Beauty portrait with a white flower',
    [0.5, 0.35],
  ),
  'glam-portrait': p(
    '800x1200',
    ['beauty', 'people'],
    'Glamour portrait on a deep red background',
    [0.5, 0.3],
  ),
  'vr-headset': p(
    '1200x800',
    ['business', 'people'],
    'Person in a glowing VR headset, neon pink',
    [0.45, 0.4],
  ),
  'trading-screen': p('1200x845', ['business'], 'Stock chart on a laptop in the dark', [0.7, 0.5]),
  pizza: p('1200x800', ['food'], 'Homemade pizza with cherry tomatoes on a board'),
  strawberries: p('1200x800', ['food'], 'Strawberries and blueberries on black'),
  cake: p('800x1200', ['food', 'holiday'], 'Pink cake decorated with macarons'),
  'birthday-cake': p('1200x800', ['food', 'holiday'], 'Birthday cake with a candle and cherries'),
  smoothie: p('1200x800', ['food'], 'Pink berry smoothie with mint'),
  'fruit-market': p('1200x800', ['food', 'travel'], 'Colourful fruit stall at a market'),
  bakery: p('1200x800', ['food'], 'Fresh buns on a baking tray'),
  'coffee-cup': p('1200x800', ['food'], 'Cup of coffee on a wooden table'),
  'coffee-beans': p('1200x801', ['food'], 'Roasted coffee beans close-up'),
  'beach-umbrella': p('800x1200', ['travel'], 'Striped umbrella on a white beach, turquoise sea'),
  venice: p('900x1200', ['travel'], 'Venice: Grand Canal and a boat'),
  'mountain-lake': p(
    '800x1200',
    ['travel', 'nature'],
    'Wooden boat on a mountain lake',
    [0.5, 0.6],
  ),
  balloons: p('1200x800', ['travel'], 'Hot-air balloons over Cappadocia at sunrise'),
  santorini: p('800x1200', ['travel'], 'White church with a blue dome, Santorini'),
  'seaside-town': p('1200x794', ['travel'], 'Colourful seaside village on cliffs'),
  'palm-sunset': p('1200x802', ['travel', 'nature'], 'Palm trees against an orange sunset'),
  apartments: p('1200x797', ['home'], 'Modern apartment buildings with balconies'),
  'cozy-house': p('1200x801', ['home'], 'Stone house with wooden doors and ivy'),
  'window-view': p('1200x900', ['home'], 'Cosy room with a window and curtains'),
  'office-building': p('1200x800', ['business', 'home'], 'Glass office building facade'),
  'remote-work': p(
    '1200x900',
    ['business', 'people'],
    'Smiling man working at a computer',
    [0.55, 0.4],
  ),
  pencils: p('1200x798', ['business'], 'Colour pencils in a cup'),
  books: p('1200x803', ['business'], 'Row of old golden books'),
  fitness: p('1200x857', ['sport', 'people'], 'Woman stretching in a dark studio', [0.45, 0.5]),
  yoga: p('1200x800', ['sport', 'people'], 'Woman doing yoga outdoors', [0.55, 0.45]),
  tennis: p('1200x893', ['sport'], 'Tennis ball on a red clay court', [0.75, 0.6]),
  football: p('1200x800', ['sport'], 'Red football on a stone'),
  surfing: p('1200x800', ['sport', 'travel'], 'Surfer riding a big turquoise wave'),
  swimming: p('1200x801', ['sport'], 'Swimmer in a pool lane'),
  fireworks: p('1200x953', ['holiday'], 'Colourful fireworks over a city at night'),
  'christmas-tree': p('800x1200', ['holiday'], 'Christmas tree with red ornaments'),
  'christmas-ball': p('1200x794', ['holiday'], 'Red Christmas bauble on a fir branch'),
  pumpkins: p('800x1200', ['holiday', 'food'], 'Autumn pumpkins and leaves'),
  'candy-hearts': p('1200x800', ['holiday', 'food'], 'Pink candy hearts (Valentine’s Day)'),
  'yellow-car': p('900x1200', ['auto'], 'Yellow classic muscle car'),
  taxi: p('1200x800', ['auto', 'travel'], 'Yellow taxi on a blue street'),
  suv: p('1200x900', ['auto', 'travel'], 'SUV parked on a rocky coast'),
  puppy: p('800x1200', ['pets'], 'Fluffy puppy walking towards the camera', [0.5, 0.65]),
  'happy-dog': p('802x1200', ['pets'], 'Happy Australian shepherd dog', [0.5, 0.28]),
  cat: p('1200x800', ['pets'], 'Tabby cat on a blue background', [0.55, 0.5]),
  guitarist: p('1200x800', ['people', 'holiday'], 'Musician playing guitar outdoors'),
  wedding: p('1200x800', ['people', 'holiday'], 'Bride and groom smiling', [0.55, 0.35]),
  family: p('1200x800', ['people', 'home'], 'Young family playing with a baby', [0.45, 0.4]),
  'rice-terraces': p('1200x800', ['nature', 'travel'], 'Green rice terraces from above'),
} satisfies Record<string, StockPhoto>;

export type PhotoId = keyof typeof PHOTOS;

export const PHOTO_BASE_URL = '/gallery/photos';

export function isPhotoId(id: string): id is PhotoId {
  return Object.prototype.hasOwnProperty.call(PHOTOS, id);
}

/** Public URL of a stock photo (or its small preview). */
export function photoUrl(id: PhotoId, thumb = false): string {
  return `${PHOTO_BASE_URL}/${thumb ? 'thumbs/' : ''}${id}.jpg`;
}
